import { memo, useCallback, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, Heading, Text, Tooltip, TooltipProps, TooltipTrigger } from '@/components/core';
import { AIModelIcon, CloseIcon } from '@/components/icons';
import { useGetAgentRunVariables, useTour } from '@/hooks';
import { useFilteredAgentRunsQuery } from '@/hooks/api';
import { OmitChildren } from '@/utils';

export type SampleDocumentTooltipProps = OmitChildren<TooltipProps> & {
  agentId: string;
};

const ISampleDocumentTooltip = ({
  agentId,
  placement = 'bottom left',
  className,
  ...rest
}: SampleDocumentTooltipProps) => {
  const { showSampleDocumentTooltip, setShowSampleDocumentTooltip } = useTour({ prefixes: [agentId] });
  const close = useCallback(() => setShowSampleDocumentTooltip(false), [setShowSampleDocumentTooltip]);
  const { data } = useFilteredAgentRunsQuery({ agentId });
  const runs = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data]);
  const { get: getVariable } = useGetAgentRunVariables({ agentId, runId: runs?.[0]?.runId });

  if (!showSampleDocumentTooltip || !getVariable('isSampleDocument') || runs.length > 1) return <div />;

  return (
    <TooltipTrigger isOpen={showSampleDocumentTooltip} isDisabled={!showSampleDocumentTooltip}>
      <Tooltip
        className={merge('gap-sm flex px-2 pt-3 pb-2', className)}
        variant="primary"
        placement={placement}
        {...rest}
      >
        <AIModelIcon />
        <Flex col className="gap-xs max-w-[18rem]">
          <Flex className="gap-sm items-center">
            <Heading size="sm">We added a sample document for you</Heading>
            <div className="grow" />
            <Button variant="plain" onPress={close}>
              <CloseIcon />
            </Button>
          </Flex>
          <Text className="!text-gray-400">
            The file will be processed by the AI and flagged for human review. When it's ready, click to see the
            results.
          </Text>
          <Button className="w-fit self-end" onPress={close}>
            <Text>Got it!</Text>
          </Button>
        </Flex>
      </Tooltip>
      <Focusable>
        <div role="button" className="w-full" />
      </Focusable>
    </TooltipTrigger>
  );
};

export const SampleDocumentTooltip = memo(ISampleDocumentTooltip) as typeof ISampleDocumentTooltip;
